// BlockedDrugsPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";

import MakeAPICall from "../../apiCalls/common";
import Loader from "../../components/loader";
import ICInput from "../../components/form/input";
import ICCheckbox from "../../components/form/checkbox";
import SearchInput from "../../components/searchInput";
import Label from "../../components/label";
import Model from "../../components/modal";
import ICButton from "../../components/button";

import {
  getBlockedDrugsApiRoute,
  saveBlockedDrugsApiRoute,
  updateScheduleConfigurationApiRoute
} from "../../apiRoutes";

// Constants
const REGISTRY_TYPES = [
  "INSTANT_CONSULT",
  "SMOKING_CESSATION", 
  "MEDICINAL_CANNABIS",
  "INSTANT_HUSH"
];

const DEFAULT_REGISTRY = {
  INSTANT_CONSULT: { isBlocked: false, allowedAndShowWarning: false },
  SMOKING_CESSATION: { isBlocked: false, allowedAndShowWarning: false },
  MEDICINAL_CANNABIS: { isBlocked: false, allowedAndShowWarning: false },
  INSTANT_HUSH: { isBlocked: false, allowedAndShowWarning: false }
};

// Validation schemas
const drugValidationSchema = Yup.object({
  drugName: Yup.string().trim().required("Ingredient name is required"),
  message: Yup.string().trim().required("Message is required")
});

const productValidationSchema = Yup.object({
  productName: Yup.string().trim().required("Brand name is required"),
  message: Yup.string().trim().required("Message is required")
});

const scheduleValidationSchema = Yup.object({
  scheduleName: Yup.string().trim().required("Schedule is required"),
  message: Yup.string().trim().required("Message is required")
});

const BlockedDrugsPage = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [apiCallParams, setApiCallParams] = useState(null);
  const [blockedDrugsData, setBlockedDrugsData] = useState({
    blockedDrugs: [],
    blockedProducts: [],
    blockedDrugIDs: [],
    blockedProductIDs: []
  });
  const [scheduleConfigurations, setScheduleConfigurations] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [showAddDrugModal, setShowAddDrugModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showAddScheduleModal, setShowAddScheduleModal] = useState(false);
  const [showAddDrugIDModal, setShowAddDrugIDModal] = useState(false);
  const [showAddProductIDModal, setShowAddProductIDModal] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [modalText, setModalText] = useState("");

  // Memoized initial values for forms
  const formInitialValues = useMemo(() => ({
    drug: {
      drugName: "",
      message: "",
      ...Object.keys(DEFAULT_REGISTRY).reduce((acc, key) => {
        acc[`${key}_isBlocked`] = false;
        acc[`${key}_allowedAndShowWarning`] = false;
        return acc;
      }, {})
    },
    product: {
      productName: "",
      message: "",
      ...Object.keys(DEFAULT_REGISTRY).reduce((acc, key) => {
        acc[`${key}_isBlocked`] = false;
        acc[`${key}_allowedAndShowWarning`] = false;
        return acc;
      }, {})
    },
    schedule: {
      scheduleName: "",
      message: "",
      ...Object.keys(DEFAULT_REGISTRY).reduce((acc, key) => {
        acc[`${key}_isBlocked`] = false;
        acc[`${key}_allowedAndShowWarning`] = false;
        return acc;
      }, {})
    },
    drugID: {
      drugName: "",
      message: "",
      ...Object.keys(DEFAULT_REGISTRY).reduce((acc, key) => {
        acc[`${key}_isBlocked`] = false;
        acc[`${key}_allowedAndShowWarning`] = false;
        return acc;
      }, {})
    },
    productID: {
      productName: "",
      message: "",
      ...Object.keys(DEFAULT_REGISTRY).reduce((acc, key) => {
        acc[`${key}_isBlocked`] = false;
        acc[`${key}_allowedAndShowWarning`] = false;
        return acc;
      }, {})
    }
  }), []);

  // Form configurations
  const drugFormik = useFormik({
    initialValues: formInitialValues.drug,
    validationSchema: drugValidationSchema,
    onSubmit: handleAddDrug
  });

  const productFormik = useFormik({
    initialValues: formInitialValues.product,
    validationSchema: productValidationSchema,
    onSubmit: handleAddProduct
  });

  const scheduleFormik = useFormik({
    initialValues: formInitialValues.schedule,
    validationSchema: scheduleValidationSchema,
    onSubmit: handleAddSchedule
  });

  const drugIDFormik = useFormik({
    initialValues: formInitialValues.drugID,
    validationSchema: drugValidationSchema,
    onSubmit: handleAddDrugID
  });

  const productIDFormik = useFormik({
    initialValues: formInitialValues.productID,
    validationSchema: productValidationSchema,
    onSubmit: handleAddProductID
  });

  // Memoized filtered data for performance
  const { filteredDrugs, filteredProducts, filteredSchedules, filteredDrugIDs, filteredProductIDs } = useMemo(() => {
    if (!searchText) {
      return {
        filteredDrugs: blockedDrugsData.blockedDrugs,
        filteredProducts: blockedDrugsData.blockedProducts,
        filteredSchedules: scheduleConfigurations,
        filteredDrugIDs: blockedDrugsData.blockedDrugIDs,
        filteredProductIDs: blockedDrugsData.blockedProductIDs
      };
    }

    const searchLower = searchText.toLowerCase();
    return {
      filteredDrugs: blockedDrugsData.blockedDrugs.filter(drug =>
        drug.drugName.toLowerCase().includes(searchLower) ||
        drug.message.toLowerCase().includes(searchLower)
      ),
      filteredProducts: blockedDrugsData.blockedProducts.filter(product =>
        product.productName.toLowerCase().includes(searchLower) ||
        product.message.toLowerCase().includes(searchLower)
      ),
      filteredSchedules: scheduleConfigurations.filter(schedule =>
        schedule.schedule.toLowerCase().includes(searchLower) ||
        (schedule.registry && Object.values(schedule.registry).some(reg =>
          reg.message && reg.message.toLowerCase().includes(searchLower)
        ))
      ),
      filteredDrugIDs: blockedDrugsData.blockedDrugIDs.filter(drug =>
        drug.id.toLowerCase().includes(searchLower) ||
        drug.message.toLowerCase().includes(searchLower)
      ),
      filteredProductIDs: blockedDrugsData.blockedProductIDs.filter(product =>
        product.id.toLowerCase().includes(searchLower) ||
        product.message.toLowerCase().includes(searchLower)
      )
    };
  }, [blockedDrugsData, scheduleConfigurations, searchText]);

  // Load initial data from API
  useEffect(() => {
    loadBlockedDrugsData(); // Now loads all data including schedules
  }, []);

  // Success message helper
  const showSuccessMessage = useCallback((message) => {
    setModalText(message);
    setShowTextModal(true);
  }, []);

  // API Functions
  const loadBlockedDrugsData = useCallback(() => {
    setIsProcessing(true);
    setApiCallParams({
      reqType: "GET",
      reqUrl: getBlockedDrugsApiRoute,
      successCallback: (res) => {
        setIsProcessing(false);
        if (res.data && res.data.data) {
          // Transform API data - rename name to drugName/productName for local state
          const transformedData = {
            blockedDrugs: (res.data.data.blockedDrugs || []).map(drug => ({
              drugName: drug.name,
              message: drug.message,
              drugBanRegistry: drug.drugBanRegistry || {}
            })),
            blockedProducts: (res.data.data.blockedProducts || []).map(product => ({
              productName: product.name,
              message: product.message,
              drugBanRegistry: product.drugBanRegistry || {}
            })),
            blockedDrugIDs: (res.data.data.blockedDrugIDs || []).map(drug => ({
              id: drug.id,
              message: drug.message,
              drugBanRegistry: drug.drugBanRegistry || {}
            })),
            blockedProductIDs: (res.data.data.blockedProductIDs || []).map(product => ({
              id: product.id,
              message: product.message,
              drugBanRegistry: product.drugBanRegistry || {}
            }))
          };

          setBlockedDrugsData(transformedData);

          // Also load schedules from the same response
          const schedules = res.data.data.scheduleConfiguration || res.data.data.schedules || [];
          setScheduleConfigurations(schedules);
        } else {
          setBlockedDrugsData({
            blockedDrugs: [],
            blockedProducts: [],
            blockedDrugIDs: [],
            blockedProductIDs: []
          });
          setScheduleConfigurations([]);
        }
      },
      failureCallback: (err) => {
        setIsProcessing(false);
        console.error("Error loading blocked drugs data:", err);
        setModalText("Failed to load blocked drugs data. Please try again.");
        setShowTextModal(true);
      }
    });
  }, []);


  const saveToAPI = useCallback((updatedData, successMessage) => {
    // Transform data for API - rename drugName/productName to name
    const apiData = {
      blockedDrugs: updatedData.blockedDrugs.map(drug => ({
        name: drug.drugName,
        message: drug.message,
        drugBanRegistry: drug.drugBanRegistry
      })),
      blockedProducts: updatedData.blockedProducts.map(product => ({
        name: product.productName,
        message: product.message,
        drugBanRegistry: product.drugBanRegistry
      })),
      blockedDrugIDs: updatedData.blockedDrugIDs.map(drug => ({
        id: drug.id,
        message: drug.message,
        drugBanRegistry: drug.drugBanRegistry
      })),
      blockedProductIDs: updatedData.blockedProductIDs.map(product => ({
        id: product.id,
        message: product.message,
        drugBanRegistry: product.drugBanRegistry
      }))
    };

    setIsProcessing(true);
    setApiCallParams({
      reqType: "POST",
      reqUrl: saveBlockedDrugsApiRoute,
      dataObj: apiData,
      successCallback: (res) => {
        setIsProcessing(false);
        showSuccessMessage(successMessage);
      },
      failureCallback: (err) => {
        setIsProcessing(false);
        console.error("Error saving blocked drugs data:", err);
        setModalText("Failed to save data. Please try again.");
        setShowTextModal(true);
        // Revert the local state change on failure
        loadBlockedDrugsData();
      }
    });
  }, [showSuccessMessage, loadBlockedDrugsData]);

  const saveScheduleConfiguration = useCallback((scheduleConfig, successMessage) => {
    // Send ALL existing schedules PLUS the new one
    // Add the new schedule to the existing array
    const updatedSchedules = [...scheduleConfigurations, scheduleConfig];
    
    const payload = {
      scheduleConfiguration: updatedSchedules
    };

    setIsProcessing(true);
    setApiCallParams({
      reqType: "POST",
      reqUrl: updateScheduleConfigurationApiRoute,
      dataObj: payload,
      successCallback: (res) => {
        setIsProcessing(false);
        showSuccessMessage(successMessage);
        loadBlockedDrugsData(); // Reload all data including schedules
      },
      failureCallback: (err) => {
        setIsProcessing(false);
        console.error("Error saving schedule configuration:", err);
        setModalText("Failed to save schedule configuration. Please try again.");
        setShowTextModal(true);
      }
    });
  }, [showSuccessMessage, loadBlockedDrugsData, scheduleConfigurations]);

  // Form submission handlers
  const handleDrugSubmit = useCallback(() => {
    if (!drugFormik.values.drugName.trim() || !drugFormik.values.message.trim()) {
      drugFormik.setTouched({ drugName: true, message: true });
      return;
    }
    drugFormik.handleSubmit();
  }, [drugFormik]);

  const handleProductSubmit = useCallback(() => {
    if (!productFormik.values.productName.trim() || !productFormik.values.message.trim()) {
      productFormik.setTouched({ productName: true, message: true });
      return;
    }
    productFormik.handleSubmit();
  }, [productFormik]);

  const handleScheduleSubmit = useCallback(() => {
    if (!scheduleFormik.values.scheduleName.trim() || !scheduleFormik.values.message.trim()) {
      scheduleFormik.setTouched({ scheduleName: true, message: true });
      return;
    }
    scheduleFormik.handleSubmit();
  }, [scheduleFormik]);

  const handleDrugIDSubmit = useCallback(() => {
    if (!drugIDFormik.values.drugName.trim() || !drugIDFormik.values.message.trim()) {
      drugIDFormik.setTouched({ drugName: true, message: true });
      return;
    }
    drugIDFormik.handleSubmit();
  }, [drugIDFormik]);

  const handleProductIDSubmit = useCallback(() => {
    if (!productIDFormik.values.productName.trim() || !productIDFormik.values.message.trim()) {
      productIDFormik.setTouched({ productName: true, message: true });
      return;
    }
    productIDFormik.handleSubmit();
  }, [productIDFormik]);

  // CRUD operations
  function handleAddDrug(values) {
    const newDrug = {
      drugName: values.drugName.trim(),
      message: values.message.trim(),
      drugBanRegistry: {}
    };

    REGISTRY_TYPES.forEach(type => {
      newDrug.drugBanRegistry[type] = {
        isBlocked: values[`${type}_isBlocked`] || false,
        allowedAndShowWarning: values[`${type}_allowedAndShowWarning`] || false
      };
    });

    // Update local state immediately
    const updatedData = {
      blockedDrugs: [...blockedDrugsData.blockedDrugs, newDrug],
      blockedProducts: [...blockedDrugsData.blockedProducts],
      blockedDrugIDs: [...blockedDrugsData.blockedDrugIDs],
      blockedProductIDs: [...blockedDrugsData.blockedProductIDs]
    };

    setBlockedDrugsData(updatedData);
    setShowAddDrugModal(false);
    drugFormik.resetForm();

    // Save to API
    saveToAPI(updatedData, "Drug added successfully!");
  }

  function handleAddProduct(values) {
    const newProduct = {
      productName: values.productName.trim(),
      message: values.message.trim(),
      drugBanRegistry: {}
    };

    REGISTRY_TYPES.forEach(type => {
      newProduct.drugBanRegistry[type] = {
        isBlocked: values[`${type}_isBlocked`] || false,
        allowedAndShowWarning: values[`${type}_allowedAndShowWarning`] || false
      };
    });

    // Update local state immediately
    const updatedData = {
      blockedDrugs: [...blockedDrugsData.blockedDrugs],
      blockedProducts: [...blockedDrugsData.blockedProducts, newProduct],
      blockedDrugIDs: [...blockedDrugsData.blockedDrugIDs],
      blockedProductIDs: [...blockedDrugsData.blockedProductIDs]
    };

    setBlockedDrugsData(updatedData);
    setShowAddProductModal(false);
    productFormik.resetForm();

    // Save to API
    saveToAPI(updatedData, "Product added successfully!");
  }

  function handleAddSchedule(values) {
    // Create schedule configuration based on the API format you provided
    const scheduleConfig = {
      schedule: values.scheduleName.trim(),
      registry: {}
    };

    // Build registry object with each registry type
    REGISTRY_TYPES.forEach(type => {
      scheduleConfig.registry[type] = {
        isBlocked: values[`${type}_isBlocked`] || false,
        allowedAndShowWarning: values[`${type}_allowedAndShowWarning`] || false,
        message: values.message.trim()
      };
    });

    setShowAddScheduleModal(false);
    scheduleFormik.resetForm();

    // Save to API using the separate schedule configuration endpoint
    saveScheduleConfiguration(scheduleConfig, "Schedule configuration added successfully!");
  }

  function handleAddDrugID(values) {
    const newDrugID = {
      id: values.drugName.trim(),
      message: values.message.trim(),
      drugBanRegistry: {}
    };

    REGISTRY_TYPES.forEach(type => {
      newDrugID.drugBanRegistry[type] = {
        isBlocked: values[`${type}_isBlocked`] || false,
        allowedAndShowWarning: values[`${type}_allowedAndShowWarning`] || false
      };
    });

    // Update local state immediately
    const updatedData = {
      blockedDrugs: [...blockedDrugsData.blockedDrugs],
      blockedProducts: [...blockedDrugsData.blockedProducts],
      blockedDrugIDs: [...blockedDrugsData.blockedDrugIDs, newDrugID],
      blockedProductIDs: [...blockedDrugsData.blockedProductIDs]
    };

    setBlockedDrugsData(updatedData);
    setShowAddDrugIDModal(false);
    drugIDFormik.resetForm();

    // Save to API
    saveToAPI(updatedData, "Drug ID added successfully!");
  }

  function handleAddProductID(values) {
    const newProductID = {
      id: values.productName.trim(),
      message: values.message.trim(),
      drugBanRegistry: {}
    };

    REGISTRY_TYPES.forEach(type => {
      newProductID.drugBanRegistry[type] = {
        isBlocked: values[`${type}_isBlocked`] || false,
        allowedAndShowWarning: values[`${type}_allowedAndShowWarning`] || false
      };
    });

    // Update local state immediately
    const updatedData = {
      blockedDrugs: [...blockedDrugsData.blockedDrugs],
      blockedProducts: [...blockedDrugsData.blockedProducts],
      blockedDrugIDs: [...blockedDrugsData.blockedDrugIDs],
      blockedProductIDs: [...blockedDrugsData.blockedProductIDs, newProductID]
    };

    setBlockedDrugsData(updatedData);
    setShowAddProductIDModal(false);
    productIDFormik.resetForm();

    // Save to API
    saveToAPI(updatedData, "Product ID added successfully!");
  }

  const handleDeleteDrug = (index) => {
    // Update local state immediately
    const updatedData = {
      blockedDrugs: blockedDrugsData.blockedDrugs.filter((_, i) => i !== index),
      blockedProducts: [...blockedDrugsData.blockedProducts],
      blockedDrugIDs: [...blockedDrugsData.blockedDrugIDs],
      blockedProductIDs: [...blockedDrugsData.blockedProductIDs]
    };

    setBlockedDrugsData(updatedData);

    // Save to API
    saveToAPI(updatedData, "Drug deleted successfully!");
  };

  const handleDeleteProduct = (index) => {
    // Update local state immediately
    const updatedData = {
      blockedDrugs: [...blockedDrugsData.blockedDrugs],
      blockedProducts: blockedDrugsData.blockedProducts.filter((_, i) => i !== index),
      blockedDrugIDs: [...blockedDrugsData.blockedDrugIDs],
      blockedProductIDs: [...blockedDrugsData.blockedProductIDs]
    };

    setBlockedDrugsData(updatedData);

    // Save to API
    saveToAPI(updatedData, "Product deleted successfully!");
  };

  const handleDeleteDrugID = (index) => {
    // Update local state immediately
    const updatedData = {
      blockedDrugs: [...blockedDrugsData.blockedDrugs],
      blockedProducts: [...blockedDrugsData.blockedProducts],
      blockedDrugIDs: blockedDrugsData.blockedDrugIDs.filter((_, i) => i !== index),
      blockedProductIDs: [...blockedDrugsData.blockedProductIDs]
    };

    setBlockedDrugsData(updatedData);

    // Save to API
    saveToAPI(updatedData, "Drug ID deleted successfully!");
  };

  const handleDeleteProductID = (index) => {
    // Update local state immediately
    const updatedData = {
      blockedDrugs: [...blockedDrugsData.blockedDrugs],
      blockedProducts: [...blockedDrugsData.blockedProducts],
      blockedDrugIDs: [...blockedDrugsData.blockedDrugIDs],
      blockedProductIDs: blockedDrugsData.blockedProductIDs.filter((_, i) => i !== index)
    };

    setBlockedDrugsData(updatedData);

    // Save to API
    saveToAPI(updatedData, "Product ID deleted successfully!");
  };

  const handleDeleteSchedule = (index) => {
    // Remove the item from the array and send the entire remaining array
    // This matches how blocked drugs and products deletion works
    const updatedSchedules = scheduleConfigurations.filter((_, i) => i !== index);
    
    // Send the entire remaining array to the API
    const payload = {
      scheduleConfiguration: updatedSchedules
    };

    setIsProcessing(true);
    setApiCallParams({
      reqType: "POST",
      reqUrl: updateScheduleConfigurationApiRoute,
      dataObj: payload,
      successCallback: (res) => {
        setIsProcessing(false);
        showSuccessMessage("Schedule configuration deleted successfully!");
        loadBlockedDrugsData(); // Reload all data including schedules
      },
      failureCallback: (err) => {
        setIsProcessing(false);
        console.error("Error deleting schedule configuration:", err);
        setModalText("Failed to delete schedule configuration. Please try again.");
        setShowTextModal(true);
      }
    });
  };

  // Modal close handlers
  const closeDrugModal = useCallback(() => {
    setShowAddDrugModal(false);
    drugFormik.resetForm();
  }, [drugFormik]);

  const closeProductModal = useCallback(() => {
    setShowAddProductModal(false);
    productFormik.resetForm();
  }, [productFormik]);

  const closeScheduleModal = useCallback(() => {
    setShowAddScheduleModal(false);
    scheduleFormik.resetForm();
  }, [scheduleFormik]);

  const closeDrugIDModal = useCallback(() => {
    setShowAddDrugIDModal(false);
    drugIDFormik.resetForm();
  }, [drugIDFormik]);

  const closeProductIDModal = useCallback(() => {
    setShowAddProductIDModal(false);
    productIDFormik.resetForm();
  }, [productIDFormik]);

  // Registry cell renderer
  const renderRegistryCell = useCallback((item, type, index, isProduct = false) => {
    const registry = item.drugBanRegistry[type] || { 
      isBlocked: false, 
      allowedAndShowWarning: false 
    };
    
    return (
      <td key={type} style={{ width: "120px", padding: "12px", textAlign: "center", verticalAlign: "middle" }}>
        <div className="d-flex flex-column align-items-center" style={{ opacity: 0.6, pointerEvents: "none" }}>
          <div className="custom-chk-lbl mb-2">
            <ICCheckbox
              style={{ padding: 0, paddingRight: "5px" }}
              disabled={true}
              value={registry.isBlocked}
              inputProps={{ "aria-label": "Is Blocked" }}
              label="Blocked"
            />
          </div>
          <div className="custom-chk-lbl">
            <ICCheckbox
              style={{ padding: 0, paddingRight: "5px" }}
              disabled={true}
              value={registry.allowedAndShowWarning}
              inputProps={{ "aria-label": "Show Warning" }}
              label="Warning"
            />
          </div>
        </div>
      </td>
    );
  }, []);

  // Registry type cell renderer - for registry columns (Instant Consult, etc.)
  const renderRegistryTypeCell = useCallback((item, registryType, index) => {
    const registry = item.registry && item.registry[registryType] ? item.registry[registryType] : { 
      isBlocked: false, 
      allowedAndShowWarning: false,
      message: ""
    };
    
    return (
      <td key={registryType} style={{ width: "120px", padding: "12px", textAlign: "center", verticalAlign: "middle" }}>
        <div className="d-flex flex-column align-items-center" style={{ opacity: 0.6, pointerEvents: "none" }}>
          <div className="custom-chk-lbl mb-2">
            <ICCheckbox
              style={{ padding: 0, paddingRight: "5px" }}
              disabled={true}
              value={registry.isBlocked}
              inputProps={{ "aria-label": "Is Blocked" }}
              label="Blocked"
            />
          </div>
          <div className="custom-chk-lbl">
            <ICCheckbox
              style={{ padding: 0, paddingRight: "5px" }}
              disabled={true}
              value={registry.allowedAndShowWarning}
              inputProps={{ "aria-label": "Show Warning" }}
              label="Warning"
            />
          </div>
        </div>
      </td>
    );
  }, []);

  // Registry form section renderer
  const renderRegistryCards = useCallback((formik) => (
    <div className="row mt-3">
      {REGISTRY_TYPES.map(type => (
        <div key={type} className="col-md-6 mb-3">
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">{type.replace('_', ' ')}</h6>
            </div>
            <div className="card-body">
              <div className="custom-chk-lbl mb-2">
                <ICCheckbox
                  style={{ padding: 0, paddingRight: "5px" }}
                  name={`${type}_isBlocked`}
                  onChange={formik.handleChange}
                  value={formik.values[`${type}_isBlocked`]}
                  label="Is Blocked"
                />
              </div>
              <div className="custom-chk-lbl">
                <ICCheckbox
                  style={{ padding: 0, paddingRight: "5px" }}
                  name={`${type}_allowedAndShowWarning`}
                  onChange={formik.handleChange}
                  value={formik.values[`${type}_allowedAndShowWarning`]}
                  label="Show Warning"
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  ), []);

  // Schedule form section renderer
  const renderScheduleCards = useCallback((formik) => (
    <div className="row mt-3">
      {REGISTRY_TYPES.map(type => (
        <div key={type} className="col-md-6 mb-3">
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">{type.replace('_', ' ')}</h6>
            </div>
            <div className="card-body">
              <div className="custom-chk-lbl mb-2">
                <ICCheckbox
                  style={{ padding: 0, paddingRight: "5px" }}
                  name={`${type}_isBlocked`}
                  onChange={formik.handleChange}
                  value={formik.values[`${type}_isBlocked`]}
                  label="Is Blocked"
                />
              </div>
              <div className="custom-chk-lbl">
                <ICCheckbox
                  style={{ padding: 0, paddingRight: "5px" }}
                  name={`${type}_allowedAndShowWarning`}
                  onChange={formik.handleChange}
                  value={formik.values[`${type}_allowedAndShowWarning`]}
                  label="Show Warning"
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  ), []);

  return (
    <div className="view-ticket-container">
      <style>
        {`
          .view-ticket-container .table tbody tr {
            cursor: default !important;
          }
          .view-ticket-container .table-striped tbody tr:hover {
            background-color: inherit !important;
          }
        `}
      </style>

      {isProcessing && <Loader />}
      
      <div className={isProcessing ? "disableContainer" : ""}>
        <h4 className="mb-4">Blocked Ingredients & Blocked Brands</h4>

        {/* Search */}
        <div className="row mb-4">
          <label className="col-md-2 mt-2 col-form-label">Search:</label>
          <div className="col-md-6 position-relative">
            <SearchInput
              placeholder="Search ingredients and brands..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-100 font-14"
              autoComplete="off"
            />
          </div>
        </div>

        {/* Blocked Drugs Table */}
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Blocked Ingredients List</h5>
            <ICButton 
              clickEvent={() => setShowAddDrugModal(true)}
              title="Add Ingredient"
              className="btn-primary btn-sm"
            />
          </div>
          
          <div className="table-container mt-4" style={{ maxHeight: "400px", overflowY: "auto" }}>
            <table className="table table-bordered table-striped">
              <thead style={{ position: "sticky", top: "0", backgroundColor: "#fff", zIndex: 10 }}>
                <tr>
                  <th style={{ width: "15%", textAlign: "left", verticalAlign: "middle" }}>Ingredient name</th>
                  <th style={{ width: "20%", textAlign: "left", verticalAlign: "middle" }}>Message</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Instant Consult</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Smoking Cessation</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Medicinal Cannabis</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Instant Hush</th>
                  <th style={{ width: "5%", textAlign: "center", verticalAlign: "middle" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDrugs.length > 0 ? (
                  filteredDrugs.map((drug, index) => {
                    const originalIndex = blockedDrugsData.blockedDrugs.findIndex(
                      originalDrug => originalDrug.drugName === drug.drugName && 
                                     originalDrug.message === drug.message
                    );
                    
                    return (
                      <tr key={`drug-${originalIndex}`}>
                        <td className="font-14" style={{ verticalAlign: "middle", padding: "12px" }}>
                          {drug.drugName}
                        </td>
                        <td className="font-14" style={{ verticalAlign: "middle", padding: "12px" }}>
                          {drug.message}
                        </td>
                        {REGISTRY_TYPES.map(type => renderRegistryCell(drug, type, originalIndex, false))}
                        <td style={{ textAlign: "center", verticalAlign: "middle", padding: "12px" }}>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteDrug(originalIndex)}
                            title="Delete Drug"
                            style={{ cursor: "pointer" }}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr style={{ backgroundColor: "#fff" }}>
                    <td style={{ border: "none" }} className="font-14" colSpan="7">
                      {searchText ? "No drugs found matching your search" : "No blocked drugs available"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Blocked Products Table */}
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Blocked Brands List</h5>
            <ICButton 
              clickEvent={() => setShowAddProductModal(true)}
              title="Add Brand"
              className="btn-primary btn-sm"
            />
          </div>
          
          <div className="table-container mt-4" style={{ maxHeight: "400px", overflowY: "auto" }}>
            <table className="table table-bordered table-striped">
              <thead style={{ position: "sticky", top: "0", backgroundColor: "#fff", zIndex: 10 }}>
                <tr>
                  <th style={{ width: "15%", textAlign: "left", verticalAlign: "middle" }}>Brand name</th>
                  <th style={{ width: "20%", textAlign: "left", verticalAlign: "middle" }}>Message</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Instant Consult</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Smoking Cessation</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Medicinal Cannabis</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Instant Hush</th>
                  <th style={{ width: "5%", textAlign: "center", verticalAlign: "middle" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length > 0 ? (
                  filteredProducts.map((product, index) => {
                    const originalIndex = blockedDrugsData.blockedProducts.findIndex(
                      originalProduct => originalProduct.productName === product.productName && 
                                        originalProduct.message === product.message
                    );
                    
                    return (
                      <tr key={`product-${originalIndex}`}>
                        <td className="font-14" style={{ verticalAlign: "middle", padding: "12px" }}>
                          {product.productName}
                        </td>
                        <td className="font-14" style={{ verticalAlign: "middle", padding: "12px" }}>
                          {product.message}
                        </td>
                        {REGISTRY_TYPES.map(type => renderRegistryCell(product, type, originalIndex, true))}
                        <td style={{ textAlign: "center", verticalAlign: "middle", padding: "12px" }}>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteProduct(originalIndex)}
                            title="Delete Product"
                            style={{ cursor: "pointer" }}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr style={{ backgroundColor: "#fff" }}>
                    <td style={{ border: "none" }} className="font-14" colSpan="7">
                      {searchText ? "No products found matching your search" : "No blocked products available"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        

        {/* Configure Schedules Table */}
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Configure Schedules</h5>
            <ICButton 
              clickEvent={() => setShowAddScheduleModal(true)}
              title="Add Schedule"
              className="btn-primary btn-sm"
            />
          </div>
          
          <div className="table-container mt-4" style={{ maxHeight: "400px", overflowY: "auto" }}>
            <table className="table table-bordered table-striped">
              <thead style={{ position: "sticky", top: "0", backgroundColor: "#fff", zIndex: 10 }}>
                <tr>
                  <th style={{ width: "15%", textAlign: "left", verticalAlign: "middle" }}>Schedule</th>
                  <th style={{ width: "20%", textAlign: "left", verticalAlign: "middle" }}>Message</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Instant Consult</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Smoking Cessation</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Medicinal Cannabis</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Instant Hush</th>
                  <th style={{ width: "5%", textAlign: "center", verticalAlign: "middle" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSchedules.length > 0 ? (
                  filteredSchedules.map((scheduleConfig, index) => {
                    const originalIndex = scheduleConfigurations.findIndex(
                      originalSchedule => originalSchedule.schedule === scheduleConfig.schedule
                    );
                    
                    return (
                      <tr key={`schedule-${originalIndex}`}>
                        <td className="font-14" style={{ verticalAlign: "middle", padding: "12px" }}>
                          {scheduleConfig.schedule}
                        </td>
                        <td className="font-14" style={{ verticalAlign: "middle", padding: "12px" }}>
                          {scheduleConfig.registry && Object.values(scheduleConfig.registry).find(reg => reg.message)?.message || ""}
                        </td>
                        {REGISTRY_TYPES.map(type => renderRegistryTypeCell(scheduleConfig, type, originalIndex))}
                        <td style={{ textAlign: "center", verticalAlign: "middle", padding: "12px" }}>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteSchedule(originalIndex)}
                            title="Delete Schedule Configuration"
                            style={{ cursor: "pointer" }}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr style={{ backgroundColor: "#fff" }}>
                    <td style={{ border: "none" }} className="font-14" colSpan="7">
                      {searchText ? "No schedule configurations found matching your search" : "No schedule configurations available"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Blocked Drug IDs Table */}
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Blocked Drug IDs List</h5>
            <ICButton
              clickEvent={() => setShowAddDrugIDModal(true)}
              title="Add Drug ID"
              className="btn-primary btn-sm"
            />
          </div>

          <div className="table-container mt-4" style={{ maxHeight: "400px", overflowY: "auto" }}>
            <table className="table table-bordered table-striped">
              <thead style={{ position: "sticky", top: "0", backgroundColor: "#fff", zIndex: 10 }}>
                <tr>
                  <th style={{ width: "15%", textAlign: "left", verticalAlign: "middle" }}>Drug ID</th>
                  <th style={{ width: "20%", textAlign: "left", verticalAlign: "middle" }}>Message</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Instant Consult</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Smoking Cessation</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Medicinal Cannabis</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Instant Hush</th>
                  <th style={{ width: "5%", textAlign: "center", verticalAlign: "middle" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDrugIDs.length > 0 ? (
                  filteredDrugIDs.map((drug, index) => {
                    const originalIndex = blockedDrugsData.blockedDrugIDs.findIndex(
                      originalDrug => originalDrug.id === drug.id &&
                                     originalDrug.message === drug.message
                    );

                    return (
                      <tr key={`drugID-${originalIndex}`}>
                        <td className="font-14" style={{ verticalAlign: "middle", padding: "12px" }}>
                          {drug.id}
                        </td>
                        <td className="font-14" style={{ verticalAlign: "middle", padding: "12px" }}>
                          {drug.message}
                        </td>
                        {REGISTRY_TYPES.map(type => renderRegistryCell(drug, type, originalIndex, false))}
                        <td style={{ textAlign: "center", verticalAlign: "middle", padding: "12px" }}>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteDrugID(originalIndex)}
                            title="Delete Drug ID"
                            style={{ cursor: "pointer" }}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr style={{ backgroundColor: "#fff" }}>
                    <td style={{ border: "none" }} className="font-14" colSpan="7">
                      {searchText ? "No drug IDs found matching your search" : "No blocked drug IDs available"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Blocked Product IDs Table */}
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Blocked Product IDs List</h5>
            <ICButton
              clickEvent={() => setShowAddProductIDModal(true)}
              title="Add Product ID"
              className="btn-primary btn-sm"
            />
          </div>

          <div className="table-container mt-4" style={{ maxHeight: "400px", overflowY: "auto" }}>
            <table className="table table-bordered table-striped">
              <thead style={{ position: "sticky", top: "0", backgroundColor: "#fff", zIndex: 10 }}>
                <tr>
                  <th style={{ width: "15%", textAlign: "left", verticalAlign: "middle" }}>Product ID</th>
                  <th style={{ width: "20%", textAlign: "left", verticalAlign: "middle" }}>Message</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Instant Consult</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Smoking Cessation</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Medicinal Cannabis</th>
                  <th style={{ width: "15%", textAlign: "center", verticalAlign: "middle" }}>Instant Hush</th>
                  <th style={{ width: "5%", textAlign: "center", verticalAlign: "middle" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProductIDs.length > 0 ? (
                  filteredProductIDs.map((product, index) => {
                    const originalIndex = blockedDrugsData.blockedProductIDs.findIndex(
                      originalProduct => originalProduct.id === product.id &&
                                        originalProduct.message === product.message
                    );

                    return (
                      <tr key={`productID-${originalIndex}`}>
                        <td className="font-14" style={{ verticalAlign: "middle", padding: "12px" }}>
                          {product.id}
                        </td>
                        <td className="font-14" style={{ verticalAlign: "middle", padding: "12px" }}>
                          {product.message}
                        </td>
                        {REGISTRY_TYPES.map(type => renderRegistryCell(product, type, originalIndex, true))}
                        <td style={{ textAlign: "center", verticalAlign: "middle", padding: "12px" }}>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteProductID(originalIndex)}
                            title="Delete Product ID"
                            style={{ cursor: "pointer" }}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr style={{ backgroundColor: "#fff" }}>
                    <td style={{ border: "none" }} className="font-14" colSpan="7">
                      {searchText ? "No product IDs found matching your search" : "No blocked product IDs available"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary */}
        <div className="row">
          <div className="col-md-3">
            <small className="text-muted">
              Showing {filteredDrugs.length} of {blockedDrugsData.blockedDrugs.length} drugs
            </small>
          </div>
          <div className="col-md-3">
            <small className="text-muted">
              Showing {filteredProducts.length} of {blockedDrugsData.blockedProducts.length} products
            </small>
          </div>
          <div className="col-md-3">
            <small className="text-muted">
              Showing {filteredSchedules.length} of {scheduleConfigurations.length} schedules
            </small>
          </div>
          <div className="col-md-3">
            <small className="text-muted">
              Showing {filteredDrugIDs.length} of {blockedDrugsData.blockedDrugIDs.length} drug IDs / {filteredProductIDs.length} of {blockedDrugsData.blockedProductIDs.length} product IDs
            </small>
          </div>
        </div>
      </div>

      {/* Add Ingredient Modal */}
      <Model
        openModal={showAddDrugModal}
        title="Add Ingredient"
        titleClass="text-center"
        cancelEvent={closeDrugModal}
        noFooterButtons={true}
        modalBodyContent={
          <div>
            <div className="row">
              <div className="col-md-6">
                <Label className="gray ex-small" title="Ingredient name" required={true} />
                <ICInput
                  {...drugFormik.getFieldProps("drugName")}
                  placeholder="Enter Ingredient name"
                  className="w-100"
                />
                {drugFormik.touched.drugName && drugFormik.errors.drugName && (
                  <Label className="small ml-1 prescription-error" title={drugFormik.errors.drugName} />
                )}
              </div>
              <div className="col-md-6">
                <Label className="gray ex-small" title="Message" required={true} />
                <ICInput
                  {...drugFormik.getFieldProps("message")}
                  placeholder="Enter message"
                  className="w-100"
                />
                {drugFormik.touched.message && drugFormik.errors.message && (
                  <Label className="small ml-1 prescription-error" title={drugFormik.errors.message} />
                )}
              </div>
            </div>
            
            {renderRegistryCards(drugFormik)}
            
            <div className="row mt-4">
              <div className="col-12 d-flex justify-content-center">
                <ICButton
                  clickEvent={handleDrugSubmit}
                  title="Add Ingredient"
                  className="btn-primary"
                />
                <ICButton
                  clickEvent={closeDrugModal}
                  title="Cancel"
                  className="btn-secondary ml-3"
                />
              </div>
            </div>
          </div>
        }
      />

      {/* Add Brand Modal */}
      <Model
        openModal={showAddProductModal}
        title="Add Brand"
        titleClass="text-center"
        cancelEvent={closeProductModal}
        noFooterButtons={true}
        modalBodyContent={
          <div>
            <div className="row">
              <div className="col-md-6">
                <Label className="gray ex-small" title="Brand name" required={true} />
                <ICInput
                  {...productFormik.getFieldProps("productName")}
                  placeholder="Enter Brand name"
                  className="w-100"
                />
                {productFormik.touched.productName && productFormik.errors.productName && (
                  <Label className="small ml-1 prescription-error" title={productFormik.errors.productName} />
                )}
              </div>
              <div className="col-md-6">
                <Label className="gray ex-small" title="Message" required={true} />
                <ICInput
                  {...productFormik.getFieldProps("message")}
                  placeholder="Enter message"
                  className="w-100"
                />
                {productFormik.touched.message && productFormik.errors.message && (
                  <Label className="small ml-1 prescription-error" title={productFormik.errors.message} />
                )}
              </div>
            </div>
            
            {renderRegistryCards(productFormik)}
            
            <div className="row mt-4">
              <div className="col-12 d-flex justify-content-center">
                <ICButton
                  clickEvent={handleProductSubmit}
                  title="Add Brand"
                  className="btn-primary"
                />

                <ICButton
                  clickEvent={closeProductModal}
                  title="Cancel"
                  className="btn-secondary ml-3"
                />

              </div>
            </div>
          </div>
        }
      />

      {/* Add Schedule Modal */}
      <Model
        openModal={showAddScheduleModal}
        title="Add Schedule"
        titleClass="text-center"
        cancelEvent={closeScheduleModal}
        noFooterButtons={true}
        modalBodyContent={
          <div>
            <div className="row">
              <div className="col-md-6">
                <Label className="gray ex-small" title="Schedule" required={true} />
                <ICInput
                  {...scheduleFormik.getFieldProps("scheduleName")}
                  placeholder="Enter Schedule (e.g., S8)"
                  className="w-100"
                />
                {scheduleFormik.touched.scheduleName && scheduleFormik.errors.scheduleName && (
                  <Label className="small ml-1 prescription-error" title={scheduleFormik.errors.scheduleName} />
                )}
              </div>
              <div className="col-md-6">
                <Label className="gray ex-small" title="Message" required={true} />
                <ICInput
                  {...scheduleFormik.getFieldProps("message")}
                  placeholder="Enter message"
                  className="w-100"
                />
                {scheduleFormik.touched.message && scheduleFormik.errors.message && (
                  <Label className="small ml-1 prescription-error" title={scheduleFormik.errors.message} />
                )}
              </div>
            </div>
            
            {renderScheduleCards(scheduleFormik)}
            
            <div className="row mt-4">
              <div className="col-12 d-flex justify-content-center">

                <ICButton
                  clickEvent={handleScheduleSubmit}
                  title="Add Schedule"
                  className="btn-primary"
                />

                <ICButton
                  clickEvent={closeScheduleModal}
                  title="Cancel"
                  className="btn-secondary ml-3"
                />

              </div>
            </div>
          </div>
        }
      />

      {/* Add Drug ID Modal */}
      <Model
        openModal={showAddDrugIDModal}
        title="Add Drug ID"
        titleClass="text-center"
        cancelEvent={closeDrugIDModal}
        noFooterButtons={true}
        modalBodyContent={
          <div>
            <div className="row">
              <div className="col-md-6">
                <Label className="gray ex-small" title="Drug ID" required={true} />
                <ICInput
                  {...drugIDFormik.getFieldProps("drugName")}
                  placeholder="Enter Drug ID"
                  className="w-100"
                />
                {drugIDFormik.touched.drugName && drugIDFormik.errors.drugName && (
                  <Label className="small ml-1 prescription-error" title={drugIDFormik.errors.drugName} />
                )}
              </div>
              <div className="col-md-6">
                <Label className="gray ex-small" title="Message" required={true} />
                <ICInput
                  {...drugIDFormik.getFieldProps("message")}
                  placeholder="Enter message"
                  className="w-100"
                />
                {drugIDFormik.touched.message && drugIDFormik.errors.message && (
                  <Label className="small ml-1 prescription-error" title={drugIDFormik.errors.message} />
                )}
              </div>
            </div>

            {renderRegistryCards(drugIDFormik)}

            <div className="row mt-4">
              <div className="col-12 d-flex justify-content-center">
                <ICButton
                  clickEvent={handleDrugIDSubmit}
                  title="Add Drug ID"
                  className="btn-primary"
                />
                <ICButton
                  clickEvent={closeDrugIDModal}
                  title="Cancel"
                  className="btn-secondary ml-3"
                />
              </div>
            </div>
          </div>
        }
      />

      {/* Add Product ID Modal */}
      <Model
        openModal={showAddProductIDModal}
        title="Add Product ID"
        titleClass="text-center"
        cancelEvent={closeProductIDModal}
        noFooterButtons={true}
        modalBodyContent={
          <div>
            <div className="row">
              <div className="col-md-6">
                <Label className="gray ex-small" title="Product ID" required={true} />
                <ICInput
                  {...productIDFormik.getFieldProps("productName")}
                  placeholder="Enter Product ID"
                  className="w-100"
                />
                {productIDFormik.touched.productName && productIDFormik.errors.productName && (
                  <Label className="small ml-1 prescription-error" title={productIDFormik.errors.productName} />
                )}
              </div>
              <div className="col-md-6">
                <Label className="gray ex-small" title="Message" required={true} />
                <ICInput
                  {...productIDFormik.getFieldProps("message")}
                  placeholder="Enter message"
                  className="w-100"
                />
                {productIDFormik.touched.message && productIDFormik.errors.message && (
                  <Label className="small ml-1 prescription-error" title={productIDFormik.errors.message} />
                )}
              </div>
            </div>

            {renderRegistryCards(productIDFormik)}

            <div className="row mt-4">
              <div className="col-12 d-flex justify-content-center">
                <ICButton
                  clickEvent={handleProductIDSubmit}
                  title="Add Product ID"
                  className="btn-primary"
                />
                <ICButton
                  clickEvent={closeProductIDModal}
                  title="Cancel"
                  className="btn-secondary ml-3"
                />
              </div>
            </div>
          </div>
        }
      />

      {/* Success/Error Modal */}
      <Model
        useOneButton={true}
        cancelText="Okay"
        openModal={showTextModal}
        cancelEvent={() => setShowTextModal(false)}
        modalBodyContent={<p className="text-center mb-0">{modalText}</p>}
      />

      {/* API Call */}
      <MakeAPICall apiParam={apiCallParams} />
    </div>
  );
};

export default BlockedDrugsPage;
